/**
 * Word Index Adjustment Tracker (Unified)
 *
 * Tracks INSERT and DELETE operations and calculates adjusted paragraph indices.
 * Solves the problem where insertions and deletions shift subsequent paragraph indices.
 *
 * SCOPE: Single chat response (AutoRedlineOutput)
 * - Resets for each new AI response
 * - Tracks INSERT operations (position: 'before' | 'after')
 * - Tracks DELETE operations (track changes aware)
 *
 * TRACK CHANGES AWARENESS:
 * - Track changes OFF: Deletions shift indices down (paragraph actually removed)
 * - Track changes ON: Deletions only mark strikethrough (no index shift, still tracked)
 *
 * KEY RULES - INSERT:
 * - INSERT BEFORE para N: Affects indices >= N (new para becomes N, old N becomes N+1)
 * - INSERT AFTER para N: Affects indices > N (new para becomes N+1, old N+1 becomes N+2)
 *
 * KEY RULES - DELETE (track changes OFF):
 * - DELETE at N affects INSERT BEFORE indices > N (strictly greater)
 * - DELETE at N affects INSERT AFTER indices >= N (greater or equal)
 * - DELETE at N affects DELETE indices > N (strictly greater)
 *
 * CRITICAL DISCOVERY: DELETE affecting INSERT has BEFORE/AFTER asymmetry!
 */

import { createLogger } from "../../../utils/logger";

const log = createLogger("UnifiedIndexTracker");

/**
 * Represents a recorded operation (insert or delete)
 */
interface RecordedOperation {
  /** Type of operation */
  type: "insert" | "delete";
  /** Original paragraph index from backend */
  originalIndex: number;
  /** Position of insertion (only for insert) */
  position?: "before" | "after";
  /** Whether track changes was ON when this operation was recorded */
  trackChangesWasOn: boolean;
}

/**
 * Unified tracker for INSERT and DELETE operations with track changes awareness
 */
export class UnifiedIndexTracker {
  private appliedOperations: RecordedOperation[] = [];
  private readonly trackChangesMode: string;

  constructor(trackChangesMode: string = "off") {
    this.trackChangesMode = trackChangesMode;
    log.debug(`Created UnifiedIndexTracker with track changes mode: ${trackChangesMode}`);
  }

  /**
   * Helper to determine if track changes is on for a given mode
   */
  private isTrackChangesOnForMode(mode: string): boolean {
    return mode === "trackAll" || mode === "trackMineOnly";
  }

  /**
   * Records an insertion that has been successfully applied
   *
   * @param originalIndex - The paragraphId from the backend
   * @param position - Whether insertion was 'before' or 'after'
   * @param currentTrackChangesMode - The track changes mode at the time of this operation (optional, defaults to tracker's mode)
   */
  recordInsertion(
    originalIndex: number,
    position: "before" | "after",
    currentTrackChangesMode?: string
  ): void {
    // Use the provided mode for this operation, or fall back to tracker's mode
    const modeToUse = currentTrackChangesMode ?? this.trackChangesMode;
    const trackChangesWasOn = this.isTrackChangesOnForMode(modeToUse);

    this.appliedOperations.push({
      type: "insert",
      originalIndex,
      position,
      trackChangesWasOn,
    });

    log.info(`CS-259: Recorded insertion ${position} paragraph ${originalIndex}`, {
      totalTracked: this.appliedOperations.length,
      trackChangesMode: modeToUse,
      trackChangesWasOn,
    });
  }

  /**
   * Records a deletion that has been successfully applied
   *
   * @param originalIndex - The paragraphId from the backend
   * @param currentTrackChangesMode - The track changes mode at the time of this operation (optional, defaults to tracker's mode)
   */
  recordDeletion(originalIndex: number, currentTrackChangesMode?: string): void {
    // Use the provided mode for this operation, or fall back to tracker's mode
    const modeToUse = currentTrackChangesMode ?? this.trackChangesMode;
    const trackChangesWasOn = this.isTrackChangesOnForMode(modeToUse);

    this.appliedOperations.push({
      type: "delete",
      originalIndex,
      trackChangesWasOn,
    });

    log.info(`CS-259: Recorded deletion of paragraph ${originalIndex}`, {
      totalTracked: this.appliedOperations.length,
      trackChangesMode: modeToUse,
      trackChangesWasOn,
    });
  }

  /**
   * Calculates the adjusted index for an INSERT operation
   *
   * LOGIC:
   * - INSERT BEFORE para N: Affects indices >= N (+1 shift)
   * - INSERT AFTER para N: Affects indices > N (+1 shift)
   * - STACKING: Multiple insertions at SAME index with SAME position stack naturally
   * - DELETE at N (track changes OFF):
   *   - Affects INSERT BEFORE indices > N (strictly greater) → -1 shift
   *   - Affects INSERT AFTER indices >= N (greater or equal) → -1 shift
   *
   * @param originalIndex - The paragraphId from the backend
   * @param position - The insertion position ('before' or 'after')
   * @returns The adjusted index accounting for all applied operations
   */
  calculateAdjustedIndexForInsert(originalIndex: number, position: "before" | "after"): number {
    let adjustment = 0;

    for (const op of this.appliedOperations) {
      if (op.type === "insert") {
        // INSERT BEFORE: affects indices >= N
        if (op.position === "before" && originalIndex >= op.originalIndex) {
          adjustment++;
        }
        // INSERT AFTER: affects indices > N
        else if (op.position === "after" && originalIndex > op.originalIndex) {
          adjustment++;
        }
        // STACKING: Multiple AFTER insertions at same index should stack
        else if (
          op.position === "after" &&
          originalIndex === op.originalIndex &&
          position === "after"
        ) {
          adjustment++;
        }
      } else if (op.type === "delete") {
        // Only affects if track changes was OFF (paragraph actually removed)
        if (!op.trackChangesWasOn) {
          // CRITICAL ASYMMETRY: BEFORE and AFTER have different rules!

          // DELETE at N affects INSERT BEFORE indices > N (strictly greater)
          if (position === "before" && originalIndex > op.originalIndex) {
            adjustment--;
          }
          // DELETE at N affects INSERT AFTER indices >= N (greater or equal)
          else if (position === "after" && originalIndex >= op.originalIndex) {
            adjustment--;
          }
        }
      }
    }

    // No Math.max needed - negative indices proven impossible with correct logic
    const adjustedIndex = originalIndex + adjustment;

    if (adjustment !== 0) {
      log.debug(
        `Insert adjustment: Original ${originalIndex} (${position}) → Adjusted ${adjustedIndex}`,
        { adjustment: adjustment > 0 ? `+${adjustment}` : adjustment }
      );
    }

    return adjustedIndex;
  }

  /**
   * Calculates the adjusted index for a DELETE operation
   *
   * LOGIC:
   * - INSERT BEFORE: shifts indices >= N up by 1
   * - INSERT AFTER: shifts indices > N up by 1
   * - DELETE at N (track changes OFF): shifts indices > N down by 1
   *
   * @param originalIndex - The paragraphId from the backend
   * @returns The adjusted index accounting for all applied operations
   */
  calculateAdjustedIndexForDelete(originalIndex: number): number {
    let adjustment = 0;

    for (const op of this.appliedOperations) {
      if (op.type === "insert") {
        // INSERT BEFORE: shifts indices >= N up
        if (op.position === "before" && originalIndex >= op.originalIndex) {
          adjustment++;
        }
        // INSERT AFTER: shifts indices > N up
        else if (op.position === "after" && originalIndex > op.originalIndex) {
          adjustment++;
        }
      } else if (op.type === "delete") {
        // Only affects if track changes was OFF
        if (!op.trackChangesWasOn) {
          // DELETE at N shifts indices > N down by 1
          if (originalIndex > op.originalIndex) {
            adjustment--;
          }
        }
      }
    }

    // No Math.max needed - negative indices proven impossible
    const adjustedIndex = originalIndex + adjustment;

    if (adjustment !== 0) {
      log.debug(`Delete adjustment: Original ${originalIndex} → Adjusted ${adjustedIndex}`, {
        adjustment: adjustment > 0 ? `+${adjustment}` : adjustment,
      });
    }

    return adjustedIndex;
  }

  /**
   * Calculates the adjusted index for an EDIT operation using paragraphId
   *
   * EDIT operations don't shift indices themselves, but need adjustment
   * if previous INSERT/DELETE operations have shifted paragraph positions.
   * Logic is identical to DELETE (find correct paragraph, don't create/remove).
   *
   * NOTE: EDIT operations do NOT record themselves in the tracker - they
   * only modify text within existing paragraphs without changing paragraph count.
   *
   * @param originalIndex - The paragraphId from the backend
   * @returns The adjusted index accounting for all applied operations
   */
  calculateAdjustedIndexForEdit(originalIndex: number): number {
    // Delegate to DELETE logic - both just need to "find the right paragraph"
    // without creating or removing paragraphs
    const adjustedIndex = this.calculateAdjustedIndexForDelete(originalIndex);

    if (adjustedIndex !== originalIndex) {
      log.debug(`Edit adjustment: Original ${originalIndex} → Adjusted ${adjustedIndex}`);
    }

    return adjustedIndex;
  }

  /**
   * BACKWARD COMPATIBILITY: Calculates the adjusted index for INSERT operations
   * Delegates to calculateAdjustedIndexForInsert
   *
   * @deprecated Use calculateAdjustedIndexForInsert instead
   */
  calculateAdjustedIndex(originalIndex: number, position: "before" | "after"): number {
    return this.calculateAdjustedIndexForInsert(originalIndex, position);
  }

  /**
   * Resets all tracked operations
   * Called when starting a new AutoRedlineOutput
   */
  reset(): void {
    const previousCount = this.appliedOperations.length;
    this.appliedOperations = [];

    if (previousCount > 0) {
      log.debug(`Reset tracker (cleared ${previousCount} operations)`);
    }
  }

  /**
   * Gets the current count of tracked operations
   * Useful for debugging and testing
   */
  getOperationCount(): number {
    return this.appliedOperations.length;
  }

  /**
   * Gets the current count of tracked insertions
   * BACKWARD COMPATIBILITY: Alias for getOperationCount filtered by type
   */
  getInsertionCount(): number {
    return this.appliedOperations.filter((op) => op.type === "insert").length;
  }

  /**
   * Gets the track changes mode this tracker was created with
   */
  getTrackChangesMode(): string {
    return this.trackChangesMode;
  }
}

// BACKWARD COMPATIBILITY: Export InsertionIndexTracker as alias
export { UnifiedIndexTracker as InsertionIndexTracker };
